import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Tag } from "lucide-react";
import { isEmail, isPhone } from "@/lib/validate";
import { useSubmission } from "@/hooks/useSubmission";
import { CTAButton } from "@/components/site/CTAButton";
import { ErrorAlert, Field, Honeypot, SuccessPanel, a11y, inputClass } from "@/components/forms/FormBits";

const EMPTY = { name: "", business: "", phone: "", email: "", message: "", fax: "" };

const validate = (v) => {
  const e = {};
  if (!v.name.trim()) e.name = "Please enter your name.";
  if (v.phone.trim() && !isPhone(v.phone)) e.phone = "Enter a valid phone number, including area code.";
  if (!v.email.trim()) e.email = "Please enter your email.";
  else if (!isEmail(v.email)) e.email = "Enter a valid email address, like you@business.com.";
  if (!v.message.trim()) e.message = "Tell us a little about what you need.";
  else if (v.message.trim().length < 10) e.message = "Add a bit more detail (at least 10 characters).";
  return e;
};

export const ContactForm = () => {
  const [params] = useSearchParams();
  const topic = (params.get("topic") || "").slice(0, 80);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { status, error, submit, reset } = useSubmission("/contact", {
    success: "Message sent",
    successDescription: "Thanks! A founder will be in touch soon.",
  });

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const found = validate(values);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`contact-${first}`)?.focus();
      return;
    }
    if (await submit({ ...values, topic })) setValues(EMPTY);
  };

  if (status === "success") {
    return (
      <SuccessPanel
        testId="contact-success"
        title="Message sent. Thank you."
        body="We've got your note, and you'll get a short confirmation email from us. A founder will get back to you soon by email or phone."
        resetLabel="Send another message"
        onReset={reset}
      />
    );
  }

  const busy = status === "submitting";
  const text = (key, label, props = {}) => (
    <Field id={`contact-${key}`} label={label} required={props.required} optional={!props.required} error={errors[key]}>
      <input
        {...a11y(`contact-${key}`, errors[key], props.required)}
        type={props.type || "text"}
        autoComplete={props.autoComplete}
        placeholder={props.placeholder}
        value={values[key]}
        onChange={set(key)}
        className={`${inputClass} h-12`}
        data-testid={`contact-${key}-input`}
      />
    </Field>
  );

  return (
    <form noValidate onSubmit={onSubmit} className="relative space-y-6" data-testid="contact-form">
      {topic && (
        <p
          className="inline-flex items-center gap-2 rounded-full border border-[#2F6BFF]/40 bg-[#2F6BFF]/10 px-3 py-1.5 text-sm text-white"
          data-testid="contact-topic"
        >
          <Tag className="h-3.5 w-3.5 text-[#2DD4D4]" aria-hidden="true" />
          About: {topic}
        </p>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        {text("name", "Name", { required: true, autoComplete: "name", placeholder: "Your name" })}
        {text("business", "Business", { autoComplete: "organization", placeholder: "Business name" })}
        {text("phone", "Phone", { type: "tel", autoComplete: "tel", placeholder: "(973) 000-0000" })}
        {text("email", "Email", { required: true, type: "email", autoComplete: "email", placeholder: "you@business.com" })}
      </div>
      <Field id="contact-message" label="Message" required error={errors.message}>
        <textarea
          {...a11y("contact-message", errors.message, true)}
          rows={5}
          placeholder="What does your business do, and what do you need help with?"
          value={values.message}
          onChange={set("message")}
          className={`${inputClass} resize-y py-3`}
          data-testid="contact-message-input"
        />
      </Field>
      <Honeypot value={values.fax} onChange={set("fax")} />
      {status === "error" && <ErrorAlert message={error} testId="contact-error" />}
      <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-[#A1A1AA]">
          Fields marked <span className="text-[#2DD4D4]">*</span> are required.
        </p>
        <CTAButton type="submit" size="lg" disabled={busy} arrow={!busy} testId="contact-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {busy ? "Sending…" : "Send message"}
        </CTAButton>
      </div>
    </form>
  );
};
